import { useParams, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { format } from 'date-fns';
import Header from '@/components/navigation/Header';
import Footer from '@/components/sections/Footer';
import { usePostBySlug } from '@/hooks/usePosts';
import { Badge } from '@/components/ui/badge';

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const { data: post, isLoading, error } = usePostBySlug(slug || '');

  if (error || (!isLoading && !post)) {
    return <Navigate to="/404" replace />;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto">
            <div className="animate-pulse">
              <div className="h-8 bg-muted rounded mb-4 w-3/4"></div>
              <div className="h-4 bg-muted rounded mb-8 w-1/2"></div>
              <div className="space-y-4">
                {[...Array(10)].map((_, i) => (
                  <div key={i} className="h-4 bg-muted rounded"></div>
                ))}
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!post) return null;

  const publishDate = post.publish_date ? new Date(post.publish_date) : new Date(post.created_at);
  const authorName = post.profiles?.full_name || 'Chief Bitcoin Officer';

  return (
    <>
      <Helmet>
        <title>{post.meta_title || post.title} | Chief Bitcoin Officer</title>
        <meta 
          name="description" 
          content={post.meta_description || post.excerpt || `Read ${post.title} on Chief Bitcoin Officer`} 
        />
        <meta property="og:title" content={post.meta_title || post.title} />
        <meta property="og:description" content={post.meta_description || post.excerpt || `Read ${post.title} on Chief Bitcoin Officer`} />
        <meta property="og:type" content="article" />
        <meta property="article:published_time" content={publishDate.toISOString()} />
        <meta property="article:author" content={authorName} />
        <link rel="canonical" href={`https://chiefbitcoinofficer.com/blog/${post.slug}`} />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": post.title,
            "description": post.meta_description || post.excerpt,
            "author": {
              "@type": "Person",
              "name": authorName
            },
            "publisher": {
              "@type": "Organization",
              "name": "Chief Bitcoin Officer",
              "url": "https://chiefbitcoinofficer.com"
            },
            "datePublished": publishDate.toISOString(),
            "dateModified": new Date(post.updated_at).toISOString(),
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": `https://chiefbitcoinofficer.com/blog/${post.slug}`
            }
          })}
        </script>
      </Helmet>
      
      <div className="min-h-screen bg-background">
        <Header />
        
        <article className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto">
            <header className="mb-12 text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
                {post.title}
              </h1>
              
              <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-muted-foreground mb-6">
                <time dateTime={publishDate.toISOString()}>
                  {format(publishDate, 'MMMM dd, yyyy')}
                </time>
                <span>•</span>
                <span>by {authorName}</span>
              </div>
              
              {post.excerpt && (
                <p className="text-xl text-muted-foreground leading-relaxed max-w-3xl mx-auto">
                  {post.excerpt}
                </p>
              )}
            </header>
            
            <div 
              className="text-only-post prose prose-lg max-w-none"
              dangerouslySetInnerHTML={{ __html: post.content || '' }}
            />
            
            <footer className="mt-16 pt-8 border-t border-border">
              <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                  Published on {format(publishDate, 'MMMM dd, yyyy')}
                </div>
                <Badge variant="secondary">
                  {post.status}
                </Badge>
              </div>
            </footer>
          </div>
        </article>
        
        <Footer />
      </div>
    </>
  );
};

export default BlogPost;